import { PolicySummary } from '@aaa-ncnu-ie/mypolicy-api-customers';
import { DriverInfo } from '@aaa-ncnu-ie/mypolicy-api-policies';
import { Box, Typography, Card, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';

import { useDriversForPolicyQuery } from '../../../api/getDriversForPolicy';
import { usePoliciesQuery } from '../../../api/getPolicies';
import ExitConfirmationModal from '../../../components/ExitConfirmationModal';
import colors from '../../../constants/colors';
import { StateCodes } from '../../../constants/fraudMessages';
import usePathnameBlocker from '../../../hooks/usePathnameBlocker';
import useUpdateTitle from '../../../hooks/useUpdateTitle';
import { GlassClaimForm } from './validations';
import CircularProgress from '../../../components/CircularProgress/CircularProgress';

type GlassClaimFormContext = {
  insuredDrivers: DriverInfo[];
  formValues: GlassClaimForm;
  setFormValues: (formValuesSubset: GlassClaimForm) => void;
  riskState?: StateCodes;
  policies: PolicySummary[];
  formSubmitted: boolean;
  setFormSubmitted: (arg: boolean) => void;
};

export const useGlassClaimForm = () =>
  useOutletContext<GlassClaimFormContext>();

const GlassClaim = () => {
  useUpdateTitle('Start Glass Claim');
  const { data, isLoading: overviewLoading } = usePoliciesQuery();
  const autoPolicies = data?.policies?.filter(
    (policy) => policy.policyType === 'Auto'
  );

  const [formValues, setFormValues] = useState<Partial<GlassClaimForm>>({});
  const [showExitDialog, setShowExitDialog] = useState(false);
  // Used to prevent exit modal from appearing when form is submitted and navigated away from
  const [formSubmitted, setFormSubmitted] = useState(false);
  const navigationBlocker = usePathnameBlocker(
    ['/logout', '/claims/glass'],
    setShowExitDialog,
    formSubmitted
  );

  useEffect(() => {
    if (data?.policies && data?.policies?.length > 0) {
      setFormValues({ policyNumber: autoPolicies?.[0]?.policyNumber ?? '' });
    }
  }, [data]);

  const { data: drivers, isLoading: policyDriversLoading } =
    useDriversForPolicyQuery(formValues?.policyNumber);

  const isLoadingRequests = overviewLoading || policyDriversLoading;

  const insuredDrivers = drivers;
  const riskState =
    formValues?.policyNumber &&
    autoPolicies &&
    autoPolicies?.find(
      (policy) => policy.policyNumber === formValues.policyNumber
    )?.riskState;

  return (
    <>
      <ExitConfirmationModal
        open={showExitDialog}
        onClose={() => {
          setShowExitDialog(false);
        }}
        handleContinue={async () => {
          navigationBlocker();
        }}
        title="Are you sure you want to leave this page?"
        bodyText={['By selecting “Yes” your claim will not be started.']}
        leaveText="Yes, leave"
        stayText="No, stay"
        showRemoveIcon
      />
      <Box
        sx={{
          width: 1,
          px: {
            mobile: 2,
            tablet: 4,
            desktop: 19.5,
          },
          py: {
            mobile: 2,
            tablet: 3,
            desktop: 9,
          },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: 500,
            fontSize: {
              mobile: 33,
              tablet: 44,
            },
            lineHeight: {
              mobile: '48px',
              tablet: '56px',
            },
          }}
        >
          Glass claim
        </Typography>
        <Card
          sx={{
            p: {
              mobile: 2,
              tablet: 5,
            },
            mt: {
              mobile: 2,
              tablet: 3,
            },
          }}
        >
          <form
            action={import.meta.env.VITE_SAFELITE_URL}
            method="post"
            target="_blank"
          >
            {isLoadingRequests ? (
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                <CircularProgress />
              </Stack>
            ) : (
              <Outlet
                context={{
                  insuredDrivers,
                  formValues,
                  setFormValues,
                  riskState,
                  policies: autoPolicies,
                  formSubmitted,
                  setFormSubmitted,
                }}
              />
            )}
          </form>
        </Card>
      </Box>
    </>
  );
};

export default GlassClaim;
